import { create } from "zustand";
import chatApiService from "../lib/chatApi";
import {
  initChatSocket,
  disconnectChatSocket,
  sendTyping,
  sendStopTyping,
  markChatRead,
  onReceiveMessage,
  onReceiveChannelMessage,
  onUserTyping,
  onUserStopTyping,
  onPresenceUpdate,
  onOnlineUsersSnapshot,
  onChatCreated,
  onConnectionState,
  onReconnect,
  requestOnlineUsersSnapshot,
  type SocketMessage,
} from "../lib/chatSocket";
import type {
  Chat,
  ChatMessage as ApiChatMessage,
  ChatParticipant,
} from "../lib/chatApi";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MessageType = "text" | "file" | "system";
export type ConversationType = "one-to-one" | "group" | "trip";
export type SidebarTab = "chats" | "viajes";

// Re-export for compatibility
export type { ChatParticipant };

export type MessageStatus = "pending" | "sent" | "failed";

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_name: string;
  content: string;
  type: MessageType;
  timestamp: number;
  read_by: string[];
  attachment_url?: string | null;
  attachment_name?: string | null;
  attachment_type?: string | null;
  attachment_size?: number | null;
  /** Local-only field for optimistic UI; undefined for messages from server. */
  status?: MessageStatus;
}

export type DeviceStatus = "online" | "offline" | "idle" | "moving" | "no-signal";

export interface RouteWaypoint {
  lat: number;
  lng: number;
  label?: string;
}

export interface TripVehicleInfo {
  vehicle_id: string;
  vehicle_name: string;
  vehicle_plate?: string;
  device_id: string;
  device_status: DeviceStatus;
  device_model?: string;
  trailer_id?: string;
  trailer_plate?: string;
  driver_name: string;
  driver_phone?: string;
  route_name: string;
  route_waypoints: RouteWaypoint[];
  current_position?: RouteWaypoint;
  estimated_arrival?: string;
  distance_km?: number;
}

export interface Conversation {
  id: string;
  type: ConversationType;
  name?: string | null;
  participants: ChatParticipant[];
  participant_ids: string[];
  last_message?: string | null;
  last_message_at?: number | null;
  last_message_sender_id?: string | null;
  unread_count: number;
  created_at: number;
  company_id: string;
  trip_id?: string | null;
  trip_name?: string | null;
  trip_description?: string | null;
  route_id?: string | null;
  route_name?: string | null;
  vehicle_info?: TripVehicleInfo;
}

interface SupportChatState {
  // Conversations
  conversations: Conversation[];
  activeConversationId: string | null;
  isLoadingConversations: boolean;

  // Sidebar tab
  activeTab: SidebarTab;
  setActiveTab: (tab: SidebarTab) => void;

  // Messages
  messages: ChatMessage[];
  isLoadingMessages: boolean;
  isLoadingOlderMessages: boolean;
  hasMoreMessages: boolean;
  isSending: boolean;

  // Presence
  onlineUsers: Record<string, boolean>;

  // Typing indicators
  typingUsers: Record<string, string[]>;

  // Socket connection state
  isSocketConnected: boolean;

  // Unsubscribe functions
  _unsubMessages: (() => void) | null;
  _unsubTyping: (() => void) | null;
  _unsubPresence: (() => void) | null;
  _unsubOnlineSnapshot: (() => void) | null;
  _unsubConnState: (() => void) | null;
  _fetchGen: number;

  // Actions
  initializeChat: (token: string, userId: string, companyId: string) => Promise<void>;
  fetchConversations: () => Promise<void>;
  setActiveConversation: (conversationId: string | null) => void;
  fetchMessages: (conversationId: string) => Promise<void>;
  loadOlderMessages: (conversationId: string) => Promise<number>;
  sendMessage: (params: {
    conversation_id: string;
    sender_id: string;
    sender_name: string;
    content: string;
    type?: MessageType;
    attachment_url?: string | null;
    attachment_name?: string | null;
    attachment_type?: string | null;
    attachment_size?: number | null;
  }) => Promise<void>;
  createConversation: (params: {
    type: ConversationType;
    participants: ChatParticipant[];
    company_id: string;
    name?: string;
    trip_id?: string;
    trip_name?: string;
    route_id?: string;
  }) => Promise<string>;
  retrySendMessage: (tempMessageId: string) => Promise<void>;
  removeFailedMessage: (tempMessageId: string) => void;
  markAsRead: (conversationId: string) => Promise<void>;
  searchUsers: (query: string) => Promise<ChatParticipant[]>;
  uploadFile: (file: File) => Promise<{
    file_path: string;
    file_name: string;
    file_type: string;
    file_size: number;
    category: "images" | "videos" | "audio" | "files";
  }>;

  // Typing
  setTyping: (conversationId: string, userId: string, isTyping: boolean) => void;

  // Reset
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Helpers: API <-> Store mappers
// ---------------------------------------------------------------------------
const chatToConversation = (chat: Chat): Conversation => ({
  id: chat.id,
  type: chat.type,
  name: chat.name ?? null,
  participants: chat.participants,
  participant_ids: chat.participant_ids,
  last_message: chat.last_message ?? null,
  last_message_at: chat.last_message_at ?? null,
  last_message_sender_id: chat.last_message_sender_id ?? null,
  unread_count: chat.unread_count ?? 0,
  created_at: chat.created_at,
  company_id: chat.company_id,
  trip_id: chat.trip_id ?? null,
  trip_name: chat.trip_name ?? null,
  trip_description: chat.trip_description ?? null,
  route_id: chat.route_id ?? null,
  route_name: chat.route_name ?? null,
});

const apiMessageToChatMessage = (
  msg: ApiChatMessage,
  conversation_id: string,
): ChatMessage => ({
  id: msg.id,
  conversation_id,
  sender_id: msg.sender_id,
  sender_name: msg.sender_name,
  content: msg.content,
  type: msg.type as MessageType,
  timestamp: msg.timestamp,
  read_by: msg.read_by,
  attachment_url: msg.attachment_url ?? null,
  attachment_name: msg.attachment_name ?? null,
  attachment_type: msg.attachment_type ?? null,
  attachment_size: msg.attachment_size ?? null,
});

const socketMessageToChatMessage = (m: SocketMessage): ChatMessage => ({
  id: m.id,
  conversation_id: m.chat_id,
  sender_id: m.sender_id,
  sender_name: m.sender_name,
  content: m.content,
  type: m.type,
  timestamp: m.timestamp,
  read_by: m.read_by ?? [],
  attachment_url: m.attachment_url ?? null,
  attachment_name: m.attachment_name ?? null,
  attachment_type: m.attachment_type ?? null,
  attachment_size: m.attachment_size ?? null,
});

const MESSAGES_PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useSupportChatStore = create<SupportChatState>((set, get) => ({
  conversations: [],
  activeConversationId: null,
  isLoadingConversations: false,
  activeTab: "chats",
  messages: [],
  isLoadingMessages: false,
  isLoadingOlderMessages: false,
  hasMoreMessages: false,
  isSending: false,
  onlineUsers: {},
  typingUsers: {},
  isSocketConnected: false,
  _unsubMessages: null,
  _unsubTyping: null,
  _unsubPresence: null,
  _unsubOnlineSnapshot: null,
  _unsubConnState: null,
  _fetchGen: 0,

  setActiveTab: (tab) => set({ activeTab: tab }),

  // -------------------------------------------------------------------------
  // Initialize chat (connect socket + fetch conversations)
  //
  // ORDEN IMPORTANTE: registramos TODOS los listeners ANTES de pedir el
  // snapshot inicial de presencia. Antes de este fix los listeners se
  // registraban después de `handleConnect()`, así que el `online-users`
  // que el server respondía inmediatamente llegaba al socket antes de que
  // hubiera nadie escuchando, y `onlineUsers` quedaba vacío. Resultado
  // visible: el otro participante aparecía "Desconectado" en el header
  // del chat aunque sí estuviera enviando mensajes.
  // -------------------------------------------------------------------------
  initializeChat: async (token: string, userId: string, _companyId: string) => {
    try {
      // If a previous initialization left state around, tear it down first so
      // we don't end up with two sets of socket listeners (and therefore two
      // copies of every incoming message) after a hot reload or a user switch.
      const prev = get();
      if (prev._unsubMessages) prev._unsubMessages();
      if (prev._unsubTyping) prev._unsubTyping();
      if (prev._unsubPresence) prev._unsubPresence();
      if (prev._unsubOnlineSnapshot) prev._unsubOnlineSnapshot();
      if (prev._unsubConnState) prev._unsubConnState();

      const socket = initChatSocket(token, userId);

      // -------- 1) Listeners de DATOS (se enganchan PRIMERO) ---------------

      // Tanto `user-typing` como `user-stop-typing` son prueba viviente de que
      // el peer está conectado en este instante — su socket está abierto y
      // emitiendo. Lo usamos como señal de presencia derivada para evitar
      // mostrar "Desconectado" cuando el `presence-update` se perdió o el
      // server no lo broadcasteó al usuario actual. Lo mismo aplica para
      // mensajes entrantes (`receiveMessage` / `receive-channel-message`).
      const markPeerOnline = (uid: string | undefined | null) => {
        if (!uid || uid === userId) return;
        const { onlineUsers } = get();
        if (onlineUsers[uid]) return;
        set({ onlineUsers: { ...onlineUsers, [uid]: true } });
      };

      const applyIncomingMessage = (m: SocketMessage) => {
        const state = get();
        const { activeConversationId, conversations } = state;
        const chatMessage = socketMessageToChatMessage(m);

        // Dedupe: if this message id is already present (REST returned it
        // before the socket relay arrived, or we received it on both the
        // direct + channel relays) skip the insert.
        const alreadyInState = state.messages.some(
          (existing) => existing.id === chatMessage.id,
        );

        markPeerOnline(m.sender_id);

        if (activeConversationId === m.chat_id && !alreadyInState) {
          set({ messages: [...get().messages, chatMessage] });
        }

        // Build a stable preview that matches the server-side `buildPreview`
        // so the sidebar last-message line reflects attachment-only sends.
        const previewBase =
          m.content ||
          (m.attachment_type
            ? `[${m.attachment_type.split("/")[0]}]`
            : `[${m.type || "file"}]`);

        const knownConv = conversations.find((c) => c.id === m.chat_id);
        if (!knownConv) {
          // Mensaje de un chat que aún no tenemos local (creado en otro
          // cliente). Refrescamos la lista para incorporarlo.
          get().fetchConversations();
          return;
        }

        const updated = conversations.map((conv) => {
          if (conv.id !== m.chat_id) return conv;
          return {
            ...conv,
            last_message: previewBase,
            last_message_at: m.timestamp,
            last_message_sender_id: m.sender_id,
            unread_count:
              activeConversationId === m.chat_id || m.sender_id === userId
                ? conv.unread_count
                : conv.unread_count + 1,
          };
        });

        updated.sort((a, b) => (b.last_message_at ?? 0) - (a.last_message_at ?? 0));
        set({ conversations: updated });
      };

      const unsubMessage = onReceiveMessage(applyIncomingMessage);
      const unsubChannelMessage = onReceiveChannelMessage(applyIncomingMessage);

      const unsubTyping = onUserTyping((event) => {
        markPeerOnline(event.user_id);
        const { typingUsers } = get();
        const chatTyping = typingUsers[event.chat_id] || [];
        if (!chatTyping.includes(event.user_id)) {
          set({
            typingUsers: {
              ...typingUsers,
              [event.chat_id]: [...chatTyping, event.user_id],
            },
          });
        }
      });

      const unsubStopTyping = onUserStopTyping((event) => {
        markPeerOnline(event.user_id);
        const { typingUsers } = get();
        const chatTyping = typingUsers[event.chat_id] || [];
        set({
          typingUsers: {
            ...typingUsers,
            [event.chat_id]: chatTyping.filter((id) => id !== event.user_id),
          },
        });
      });

      const unsubPresence = onPresenceUpdate((event) => {
        const { onlineUsers } = get();
        set({
          onlineUsers: {
            ...onlineUsers,
            [event.user_id]: event.is_online,
          },
        });
      });

      // Bulk presence snapshot — rebuilds the map from scratch on every
      // (re)connection so we don't carry stale "offline" entries.
      const unsubOnlineSnapshot = onOnlineUsersSnapshot((userIds) => {
        const next: Record<string, boolean> = {};
        userIds.forEach((id) => {
          next[id] = true;
        });
        set({ onlineUsers: next });
      });

      // Otro cliente creó un chat en el que somos participantes. El server
      // hace el fan-out vía `chat-created`; aquí lo upserteamos local sin
      // esperar al primer mensaje ni a un refresh manual.
      const unsubChatCreated = onChatCreated((event) => {
        if (!event?.chat_id) return;
        const incoming = event.chat as Chat | undefined;
        if (incoming && typeof incoming === "object" && incoming.id) {
          // Server mandó el chat completo: upsert directo (evitamos round-trip).
          const conv = chatToConversation(incoming);
          set((state) => {
            const idx = state.conversations.findIndex((c) => c.id === conv.id);
            let next: Conversation[];
            if (idx >= 0) {
              next = state.conversations.slice();
              next[idx] = { ...next[idx], ...conv };
            } else {
              next = [conv, ...state.conversations];
            }
            next.sort((a, b) => (b.last_message_at ?? 0) - (a.last_message_at ?? 0));
            return { conversations: next };
          });
        } else {
          // Solo `chat_id`: refrescamos la lista para tomarlo.
          get().fetchConversations();
        }
      });

      // -------- 2) Listener de estado de conexión ---------------------------

      const handleConnect = () => {
        set({ isSocketConnected: true });
        // Ask the server for the current online-users snapshot. Esto se
        // emite DESPUÉS de que `unsubOnlineSnapshot` ya está enganchado
        // (orden importante — ver doc de la función).
        requestOnlineUsersSnapshot();
      };
      const handleDisconnect = () => set({ isSocketConnected: false });

      const unsubConnState = onConnectionState(handleConnect, handleDisconnect);

      // En reconexiones manager-level, refrescamos `GET /chats` para
      // recuperar conversaciones / mensajes que llegaron mientras
      // estábamos offline (CHAT_INTEGRATION.md §7.10).
      const unsubReconnect = onReconnect(() => {
        get().fetchConversations();
      });

      // Si el socket ya estaba conectado por idempotencia de
      // `initChatSocket` (hot reload, navegación de vuelta a /support),
      // disparamos `handleConnect` manualmente para sembrar el flag y
      // pedir el snapshot inicial de presencia. Aquí ya están enganchados
      // todos los listeners de arriba.
      if (socket.connected) handleConnect();

      // -------- 3) Persistir unsubs y arrancar fetch ------------------------

      set({
        _unsubMessages: () => {
          unsubMessage();
          unsubChannelMessage();
          unsubChatCreated();
        },
        _unsubTyping: () => {
          unsubTyping();
          unsubStopTyping();
        },
        _unsubPresence: unsubPresence,
        _unsubOnlineSnapshot: unsubOnlineSnapshot,
        _unsubConnState: () => {
          unsubConnState();
          unsubReconnect();
        },
      });

      await get().fetchConversations();
    } catch (error) {
      console.error("[SupportChat] Error initializing:", error);
    }
  },

  // -------------------------------------------------------------------------
  // Fetch conversations from API
  // -------------------------------------------------------------------------
  fetchConversations: async () => {
    const gen = get()._fetchGen + 1;
    set({ isLoadingConversations: true, _fetchGen: gen });
    try {
      const chats = await chatApiService.getChats({ limit: 50 });
      // Guard: if another fetch started while this one was in-flight, discard.
      if (get()._fetchGen !== gen) return;
      const conversations = chats.map(chatToConversation);
      set({ conversations, isLoadingConversations: false });
    } catch (error) {
      if (get()._fetchGen !== gen) return;
      console.error("[SupportChat] Error fetching conversations:", error);
      set({ isLoadingConversations: false });
    }
  },

  // -------------------------------------------------------------------------
  // Set active conversation
  //
  // Idempotent: calling with the same id we're already on is a no-op so
  // that the URL `?chat=<id>` driver in SupportChatPage can re-apply on
  // every mount (including StrictMode's double-mount) without thrashing
  // scroll position or re-fetching messages.
  //
  // Tab follow-through: el sidebar filtra conversaciones por `activeTab`
  // (`chats` vs `viajes`). Cuando entramos a una conversación desde una
  // ruta — típicamente `useOpenChat → /support?chat=<id>` disparado desde
  // TripCard, TripGridCard, DriverContent, DriversSection — el caller no
  // tiene ni quiere conocer el tab; solo nos pasa el id. Si no
  // sincronizamos `activeTab` aquí, el chat abre en el panel derecho
  // pero queda "huérfano" en el sidebar (filtrado fuera del tab actual)
  // y el usuario tiene que cambiar el tab manualmente. Hacerlo en el
  // store mantiene `activeTab` y `activeConversationId` consistentes
  // desde un único punto. Para clicks que vienen del propio sidebar es
  // un no-op porque el chat clickeado ya pertenece al tab activo.
  // -------------------------------------------------------------------------
  setActiveConversation: (conversationId) => {
    if (get().activeConversationId === conversationId) return;
    const conv = conversationId
      ? get().conversations.find((c) => c.id === conversationId)
      : null;
    set({
      activeConversationId: conversationId,
      messages: [],
      hasMoreMessages: false,
      ...(conv ? { activeTab: conv.trip_id ? "viajes" : "chats" } : {}),
    });
    if (conversationId) {
      get().fetchMessages(conversationId);
    }
  },

  // -------------------------------------------------------------------------
  // Fetch messages from API (initial page)
  // -------------------------------------------------------------------------
  fetchMessages: async (conversationId: string) => {
    set({ isLoadingMessages: true });
    try {
      const apiMessages = await chatApiService.getChatMessages(conversationId, {
        limit: MESSAGES_PAGE_SIZE,
      });
      const messages = apiMessages.map((msg) =>
        apiMessageToChatMessage(msg, conversationId),
      );
      set({
        messages,
        isLoadingMessages: false,
        hasMoreMessages: messages.length === MESSAGES_PAGE_SIZE,
      });
    } catch (error) {
      console.error("[SupportChat] Error fetching messages:", error);
      set({ isLoadingMessages: false, hasMoreMessages: false });
    }
  },

  // -------------------------------------------------------------------------
  // Load older messages (pagination — for infinite scroll up)
  // Returns the number of new messages prepended.
  // -------------------------------------------------------------------------
  loadOlderMessages: async (conversationId: string) => {
    const state = get();
    if (state.isLoadingOlderMessages || !state.hasMoreMessages) return 0;
    if (state.activeConversationId !== conversationId) return 0;

    // Use the oldest persisted message (not an optimistic temp one) as cursor.
    // Otherwise we'd ask for "messages before Date.now()" which would re-fetch
    // history that's already in state and waste a roundtrip.
    const oldestConfirmed = state.messages.find(
      (m) => !m.id.startsWith("temp-"),
    );
    if (!oldestConfirmed) return 0;

    set({ isLoadingOlderMessages: true });
    try {
      const apiMessages = await chatApiService.getChatMessages(conversationId, {
        limit: MESSAGES_PAGE_SIZE,
        before: oldestConfirmed.timestamp,
      });

      // Guard: if the user switched conversations while loading, drop result.
      if (get().activeConversationId !== conversationId) {
        set({ isLoadingOlderMessages: false });
        return 0;
      }

      const existingIds = new Set(get().messages.map((m) => m.id));
      const older = apiMessages
        .map((msg) => apiMessageToChatMessage(msg, conversationId))
        .filter((m) => !existingIds.has(m.id));

      set((s) => ({
        messages: [...older, ...s.messages],
        isLoadingOlderMessages: false,
        // Backend now sends an authoritative hasMore but we keep the size
        // heuristic as fallback for older API revisions.
        hasMoreMessages: apiMessages.length === MESSAGES_PAGE_SIZE,
      }));
      return older.length;
    } catch (error) {
      console.error("[SupportChat] Error loading older messages:", error);
      set({ isLoadingOlderMessages: false });
      return 0;
    }
  },

  // -------------------------------------------------------------------------
  // Send message
  //
  // REST is the writer (Cloud Functions → Firestore). El fan-out en tiempo
  // real lo dispara el backend (POST /internal/relay al socket server) justo
  // después de persistir, así la entrega no depende del socket del emisor.
  // -------------------------------------------------------------------------
  sendMessage: async (params) => {
    set({ isSending: true });

    const tempTimestamp = Date.now();
    const tempId = `temp-${tempTimestamp}-${Math.random().toString(36).slice(2, 8)}`;

    const optimisticMessage: ChatMessage = {
      id: tempId,
      conversation_id: params.conversation_id,
      sender_id: params.sender_id,
      sender_name: params.sender_name,
      content: params.content,
      type: (params.type ?? "text") as MessageType,
      timestamp: tempTimestamp,
      read_by: [params.sender_id],
      attachment_url: params.attachment_url ?? null,
      attachment_name: params.attachment_name ?? null,
      attachment_type: params.attachment_type ?? null,
      attachment_size: params.attachment_size ?? null,
      status: "pending",
    };

    // Mirror server-side preview logic so optimistic UI matches what other
    // participants will see in their sidebar last-message line.
    const previewBase =
      params.content ||
      (params.attachment_type
        ? `[${params.attachment_type.split("/")[0]}]`
        : `[${params.type ?? "file"}]`);

    set((state) => ({
      messages: [...state.messages, optimisticMessage],
      conversations: state.conversations.map((c) =>
        c.id === params.conversation_id
          ? {
              ...c,
              last_message: previewBase,
              last_message_at: tempTimestamp,
              last_message_sender_id: params.sender_id,
            }
          : c,
      ),
    }));

    try {
      // 1) Persist via REST (source of truth).
      const saved = await chatApiService.sendMessage(params.conversation_id, {
        content: params.content,
        type: params.type ?? "text",
        attachment_url: params.attachment_url ?? undefined,
        attachment_name: params.attachment_name ?? undefined,
        attachment_type: params.attachment_type ?? undefined,
        attachment_size: params.attachment_size ?? undefined,
      });

      const finalMessage: ChatMessage = {
        ...apiMessageToChatMessage(saved, params.conversation_id),
        status: "sent",
      };

      // Replace optimistic temp with the persisted message.
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === tempId ? finalMessage : m,
        ),
        conversations: state.conversations.map((c) =>
          c.id === params.conversation_id
            ? {
                ...c,
                last_message: previewBase,
                last_message_at: finalMessage.timestamp,
                last_message_sender_id: params.sender_id,
              }
            : c,
        ),
      }));

    } catch (error) {
      console.error("[SupportChat] Error sending message:", error);
      // Mark optimistic message as failed so UI can show retry affordance.
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === tempId ? { ...m, status: "failed" } : m,
        ),
      }));
    } finally {
      set({ isSending: false });
    }
  },

  // -------------------------------------------------------------------------
  // Create conversation
  // -------------------------------------------------------------------------
  createConversation: async (params) => {
    try {
      const { chat } = await chatApiService.createChat({
        type: params.type,
        participants: params.participants,
        name: params.name,
        trip_id: params.trip_id,
        trip_name: params.trip_name,
        route_id: params.route_id,
      });

      const conversation = chatToConversation(chat);

      // Backend may return an existing 1:1 chat (HTTP 200) instead of
      // creating a new one. We must NOT prepend in that case or we'd end up
      // with the same conversation twice in the sidebar. We upsert by id
      // and re-sort by latest activity to keep the order stable.
      set((state) => {
        const existingIdx = state.conversations.findIndex(
          (c) => c.id === conversation.id,
        );
        let next: Conversation[];
        if (existingIdx >= 0) {
          next = state.conversations.slice();
          next[existingIdx] = {
            ...next[existingIdx],
            ...conversation,
            // Preserve local unread count if backend doesn't reflect it.
            unread_count: state.conversations[existingIdx].unread_count,
          };
        } else {
          next = [conversation, ...state.conversations];
        }
        next.sort(
          (a, b) => (b.last_message_at ?? 0) - (a.last_message_at ?? 0),
        );
        return { conversations: next };
      });

      return chat.id;
    } catch (error) {
      console.error("[SupportChat] Error creating conversation:", error);
      throw error;
    }
  },

  // -------------------------------------------------------------------------
  // Retry a previously failed message (still in optimistic state).
  // -------------------------------------------------------------------------
  retrySendMessage: async (tempMessageId: string) => {
    const failed = get().messages.find(
      (m) => m.id === tempMessageId && m.status === "failed",
    );
    if (!failed) return;

    // Remove the failed temp and re-send through the normal path.
    set((state) => ({
      messages: state.messages.filter((m) => m.id !== tempMessageId),
    }));

    await get().sendMessage({
      conversation_id: failed.conversation_id,
      sender_id: failed.sender_id,
      sender_name: failed.sender_name,
      content: failed.content,
      type: failed.type,
      attachment_url: failed.attachment_url,
      attachment_name: failed.attachment_name,
      attachment_type: failed.attachment_type,
      attachment_size: failed.attachment_size,
    });
  },

  removeFailedMessage: (tempMessageId: string) => {
    set((state) => ({
      messages: state.messages.filter(
        (m) => !(m.id === tempMessageId && m.status === "failed"),
      ),
    }));
  },

  // -------------------------------------------------------------------------
  // Mark as read
  // -------------------------------------------------------------------------
  markAsRead: async (conversationId: string) => {
    try {
      await chatApiService.markAsRead(conversationId);
      markChatRead(conversationId);

      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId ? { ...c, unread_count: 0 } : c,
        ),
      }));
    } catch (error) {
      console.error("[SupportChat] Error marking as read:", error);
    }
  },

  // -------------------------------------------------------------------------
  // Search users
  // -------------------------------------------------------------------------
  searchUsers: async (query: string): Promise<ChatParticipant[]> => {
    try {
      return await chatApiService.searchUsers(query);
    } catch (error) {
      console.error("[SupportChat] Error searching users:", error);
      return [];
    }
  },

  // -------------------------------------------------------------------------
  // Upload file
  // -------------------------------------------------------------------------
  uploadFile: async (file: File) => {
    try {
      return await chatApiService.uploadFile(file);
    } catch (error) {
      console.error("[SupportChat] Error uploading file:", error);
      throw error;
    }
  },

  // -------------------------------------------------------------------------
  // Typing
  // -------------------------------------------------------------------------
  setTyping: (conversationId, _userId, isTyping) => {
    if (isTyping) {
      sendTyping(conversationId);
    } else {
      sendStopTyping(conversationId);
    }
  },

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------
  reset: () => {
    const state = get();
    if (state._unsubMessages) state._unsubMessages();
    if (state._unsubTyping) state._unsubTyping();
    if (state._unsubPresence) state._unsubPresence();
    if (state._unsubOnlineSnapshot) state._unsubOnlineSnapshot();
    if (state._unsubConnState) state._unsubConnState();
    disconnectChatSocket();

    set({
      conversations: [],
      activeConversationId: null,
      isLoadingConversations: false,
      messages: [],
      isLoadingMessages: false,
      isLoadingOlderMessages: false,
      hasMoreMessages: false,
      isSending: false,
      onlineUsers: {},
      typingUsers: {},
      activeTab: "chats",
      isSocketConnected: false,
      _unsubMessages: null,
      _unsubTyping: null,
      _unsubPresence: null,
      _unsubOnlineSnapshot: null,
      _unsubConnState: null,
    });
  },
}));
